/**
 * Putting something in the diary.
 *
 * ===========================================================================
 * TWO THINGS THE FORM HAS TO SAY OUT LOUD
 * ===========================================================================
 * 1. A HEARING IS ALWAYS CLIENT-VISIBLE. `schedule_hearing` sets `visibility=client`
 *    unconditionally and writes the matter's timeline, because a client attending a
 *    hearing has to be told about it. There is therefore no visibility choice on a
 *    hearing, and the form says so rather than showing a control that would be
 *    silently overridden.
 *
 * 2. EVERYTHING ELSE DEFAULTS TO INTERNAL, and the choice is rendered as words, not a
 *    colour and not a bare "notify client" tick (CR-25). An internal review appearing
 *    on a client's timeline is a disclosure nobody chose to make, so the default
 *    withholds and the client-facing option states plainly what it does.
 * ===========================================================================
 *
 * A hearing also REQUIRES a matter — without one it cannot reach the matter's timeline
 * or the client who is attending it. The server enforces that; this validates it too,
 * so the refusal lands on the field rather than as a banner after a round trip.
 *
 * Mounted only while open, so the day square that opened it decides the default date.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  type CalendarEvent,
  type CaseListItem,
  type FirmMember,
} from '@legalcrm/api';
import {
  Badge,
  Checkbox,
  CheckboxGroup,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  Modal,
  RadioGroup,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { EVENT_TYPE_OPTIONS } from './calendarMonth';

const schema = z
  .object({
    event_type: z.string().min(1),
    title: z.string().min(1, 'Give the entry a title'),
    starts_at: z.string().min(1, 'Say when it starts'),
    ends_at: z.string(),
    case: z.string(),
    location: z.string(),
    court_name: z.string(),
    listing_reference: z.string(),
  })
  .refine((values) => values.event_type !== 'hearing' || Boolean(values.case), {
    path: ['case'],
    message:
      'A hearing belongs to a matter. Without one it cannot reach the matter’s timeline ' +
      'or the client who is attending it.',
  })
  .refine((values) => !values.ends_at || values.ends_at >= values.starts_at, {
    path: ['ends_at'],
    message: 'An entry cannot end before it starts.',
  });

type Values = z.infer<typeof schema>;

export interface NewEventModalProps {
  onClose: () => void;
  /** Local `YYYY-MM-DD` to prefill, when opened from a day square. */
  defaultDay?: string;
  cases: CaseListItem[];
  members: FirmMember[];
}

export function NewEventModal({ onClose, defaultDay, cases, members }: NewEventModalProps) {
  const api = useApi();
  const { toast } = useToast();

  const [visibility, setVisibility] = useState<'internal' | 'client'>('internal');
  const [attendees, setAttendees] = useState<string[]>([]);

  const form = useZodForm(schema, {
    defaultValues: {
      event_type: 'meeting',
      title: '',
      // A day square opens the form at 09:00 on that day: a working assumption
      // somebody can correct, rather than a field they have to fill twice.
      starts_at: defaultDay ? `${defaultDay}T09:00` : '',
      ends_at: '',
      case: '',
      location: '',
      court_name: '',
      listing_reference: '',
    },
  });

  const isHearing = form.watch('event_type') === 'hearing';

  const create = useApiMutation<CalendarEvent, Values>(
    {
      mutationFn: (values) =>
        api.post<CalendarEvent>(STAFF.calendarEvents, {
          event_type: values.event_type,
          title: values.title,
          // `datetime-local` yields a wall-clock string with no zone; `new Date` reads
          // it as local, which is what the person typing it meant, and the API is
          // given UTC.
          starts_at: new Date(values.starts_at).toISOString(),
          ends_at: values.ends_at ? new Date(values.ends_at).toISOString() : null,
          case: values.case || null,
          location: values.location,
          court_name: values.court_name,
          listing_reference: values.listing_reference,
          // A hearing's visibility belongs to the service, not to this form.
          visibility: isHearing ? undefined : visibility,
          attendees,
          // The reader's own zone, recorded beside the instant so a listing entered in
          // one office reads correctly in another.
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      invalidates: [qk.all('calendar-events'), qk.all('cases')],
    },
    {
      onSuccess: (event) => {
        toast({
          title: 'Added to the diary',
          description:
            event.visibility === 'client'
              ? 'This entry is visible to the client.'
              : 'Internal only. The client will not see this.',
          tone: 'success',
        });
        onClose();
      },
    },
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="New diary entry"
      description="Hearings, appointments, meetings and ordinary deadlines."
      size="lg"
    >
      <Form
        form={form}
        ariaLabel="New diary entry"
        error={create.error}
        onSubmit={async (values) => {
          await create.mutateAsync(values);
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <FormSelect form={form} name="event_type" label="Kind" options={EVENT_TYPE_OPTIONS} />
          <FormSelect
            form={form}
            name="case"
            label="Matter"
            placeholder={isHearing ? 'Choose a matter' : 'No matter'}
            required={isHearing}
            options={cases.map((record) => ({
              value: record.id,
              label: `${record.case_number} — ${record.title}`,
            }))}
          />
        </div>

        <FormInput form={form} name="title" label="Title" autoFocus />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormInput form={form} name="starts_at" label="Starts" type="datetime-local" />
          <FormInput
            form={form}
            name="ends_at"
            label="Ends"
            type="datetime-local"
            hint="Optional."
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormInput form={form} name="location" label="Location" />
          {isHearing && <FormInput form={form} name="court_name" label="Court" />}
        </div>

        {isHearing && (
          <FormInput
            form={form}
            name="listing_reference"
            label="Listing reference"
            hint="Optional. The court's own reference for this listing."
          />
        )}

        {/*
          The CR-25 surface. Words, in a labelled container — never colour alone, and
          never a bare "notify the client" tick, which reads as a preference rather
          than as a disclosure.
        */}
        {isHearing ? (
          <div className="rounded-lg border border-client-facing/40 bg-client-facing-subtle/40 p-3">
            <Badge tone="client-facing" size="sm">
              The client will see this
            </Badge>
            <p className="mt-2 text-sm text-ink">
              A hearing is always visible to the client and is written onto the
              matter&apos;s timeline. That is not optional — somebody attending a hearing
              has to be told it is happening.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border p-3">
            <RadioGroup
              name="visibility"
              legend="Who can see this entry"
              value={visibility}
              onChange={(value) => setVisibility(value as 'internal' | 'client')}
              options={[
                {
                  value: 'internal',
                  label: 'Internal only — the client will not see this',
                  hint: 'The default. An internal meeting on a client’s timeline is a disclosure nobody chose to make.',
                },
                {
                  value: 'client',
                  label: 'Visible to the client',
                  hint: 'Only an appointment actually reaches the client’s timeline. A meeting or deadline is recorded as visible but notifies nobody.',
                },
              ]}
            />
          </div>
        )}

        {members.length > 0 && (
          <CheckboxGroup
            legend="Who is attending"
            hint="Firm members. Adding somebody here does not put them on the matter's team."
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {members.map((member) => (
                <Checkbox
                  key={member.id}
                  // `member.user` is the User id. `member.id` is the membership id, and
                  // the attendee record wants the former — they are not interchangeable.
                  label={member.full_name || member.email}
                  checked={attendees.includes(member.user)}
                  onChange={(event) =>
                    setAttendees((current) =>
                      event.target.checked
                        ? [...current, member.user]
                        : current.filter((id) => id !== member.user),
                    )
                  }
                />
              ))}
            </div>
          </CheckboxGroup>
        )}

        <FormActions submitLabel="Add to diary" busy={create.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
